import { Box } from '@mui/material';
import { CellValueChangedEvent, ColumnMovedEvent, ColumnResizedEvent, IRowNode } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { setupGrid } from '../../common/datagrid/agGridUtils';
import { useAgTable } from '../../common/datagrid/hooks/useAgTable';
import '../../common/datagrid/table-style.css';
import { RootState } from '../../store';
import './externalPricetable.css';
import { ExternalPriceRow } from './externalPriceTypes';
import { getColumnDefs, getExternalPriceRows, getExternalPriceTable } from './redux/externalPriceSelectors';
import { externalPriceTableActions, setIsExporting, updateExternalPrice } from './redux/externalPriceSlices';

const mapStateToProps = (state: RootState) => ({
    columns: getColumnDefs(state),
    priceRows: getExternalPriceRows(state),
    externalPriceTable: getExternalPriceTable(state),
    isExporting: state.externalPrice.ui.isExporting
});

type Props = ReturnType<typeof mapStateToProps>;

const PriceTable = ({ columns, priceRows, externalPriceTable, isExporting }: Props) => {
    const [rows, setRows] = useState([] as ExternalPriceRow[]);
    const [modifiedRows, setModifiedRows] = useState({} as { [index: number]: ExternalPriceRow });

    const dispatch = useDispatch();
    // const classes = useStyles();
    const gridRef = useRef<AgGridReact>(null);
    useEffect(() => {
        const mRows = {} as { [index: number]: ExternalPriceRow };
        setRows(r => {
            if (r.length === 0) {
                return priceRows;
            }

            // Extremely important to reuse old rows to prevent all
            // rows from being re-rendered every time a change occurs.
            return priceRows.map((priceRow, index) => {
                const oldRow = r.find(x => x.productId === priceRow.productId);
                if (oldRow != null && isEqual(oldRow, priceRow)) {
                    return oldRow;
                }
                mRows[index] = priceRow;
                return priceRow;
            });
        });
        setModifiedRows(mRows);
        setupGrid();
    }, [priceRows]);
    useEffect(() => {
        const rowNodes = [] as IRowNode[];

        for (const index in modifiedRows) {
            // const node = gridRef?.current?.api.getDisplayedRowAtIndex(Number(index));
            const node = gridRef?.current?.api.getRowNode(modifiedRows[index].productId.toString());
            if (node != null) {
                rowNodes.push(node);
            }
        }
        gridRef?.current?.api?.refreshCells({ rowNodes, force: true });
    }, [modifiedRows]);

    useEffect(() => {
        if (isExporting) {
            gridRef?.current?.api?.exportDataAsCsv();
            dispatch(setIsExporting(false));
        }
    }, [isExporting, dispatch]);

    const {
        saveColumnWidth,
        resizableColumns,
        tabbing: { tabToNextCell, handleKeyDown }
    } = useAgTable({
        columns,
        rows,
        tableActions: externalPriceTableActions,
        tableSettings: externalPriceTable,
        gridRef: gridRef
    });

    const onRowsChange = useCallback(
        ({ data, rowIndex, newValue, colDef }: CellValueChangedEvent<ExternalPriceRow>) => {
            if (rowIndex != null) {
                const pricelistId = Number(colDef.field);
                const newPrice = newValue
                    ? Number((newValue.toString() as unknown as string)?.replace(',', '.'))
                    : 0;

                if (newPrice >= 0) {
                    dispatch(
                        updateExternalPrice.request({
                            extPriceId: data.externalPriceIds[pricelistId],
                            newPrice
                        })
                    );
                }
            }
        },
        [dispatch]
    );

    const [draggedColumn, setDraggedColumn] = useState(null as { colId: string; toIndex: number } | null);

    return (
        <Box style={{ height: '82vh' }}>
            <AgGridReact
                className="ag-theme-alpine"
                reactiveCustomComponents
                containerStyle={{
                    height: '82vh',
                    summaryBorderColor: '#aaa',
                    backgroundColor: 'hsl(0deg 0% 100%)',
                    headerBackgroundColor: 'hsl(0deg 0% 97.5%)'
                }}
                columnDefs={resizableColumns}
                rowData={rows}
                onFirstDataRendered={setupGrid}
                floatingFiltersHeight={30}
                onCellValueChanged={onRowsChange}
                getRowId={params => params.data.productId?.toString() ?? ''}
                onDragStopped={() => {
                    if (draggedColumn != null && draggedColumn.colId != '') {
                        dispatch(
                            externalPriceTableActions.columnsReordered({
                                sourceColumn: draggedColumn?.colId,
                                targetColumn: columns[draggedColumn?.toIndex].field ?? ''
                            })
                        );
                        setDraggedColumn(null);
                    }
                }}
                onColumnResized={(event: ColumnResizedEvent<ExternalPriceRow>) => {
                    if (
                        event.finished &&
                        event.source == 'uiColumnDragged' &&
                        event.type == 'columnResized'
                    ) {
                        const colState = event.columnApi.getColumnState();

                        const indexOfCol = colState.findIndex(c => c.colId == event.column?.getColId());
                        saveColumnWidth(indexOfCol, colState[indexOfCol]?.width ?? 80);
                    }
                }}
                onColumnMoved={(event: ColumnMovedEvent<ExternalPriceRow>) => {
                    setDraggedColumn({ colId: event.column?.getColId() ?? '', toIndex: event.toIndex ?? 0 });
                }}
                headerHeight={100}
                ref={gridRef}
                tabToNextCell={tabToNextCell}
                onCellKeyDown={handleKeyDown}
                stopEditingWhenCellsLoseFocus
                onDisplayedColumnsChanged={setupGrid}
            />
        </Box>
    );
};

export default connect(mapStateToProps)(PriceTable);
